import {useEffect, useMemo, useState} from "react";
import {Navigate, Outlet, useLocation} from "react-router-dom";
import {useAuth} from "../auth-context";
import {useClient} from "./client";

const ACTIVE_STATUSES = new Set([
    "active",
    "trialing",
    "paid",
    "succeeded",
    "complete",
]);

const isActiveSubscription = (data) => {
    if (!data) return false;
    if (data.active === true || data.isActive === true) return true;
    const status =
        String(data.status ?? data.subscriptionStatus ?? data.state ?? "").toLowerCase();
    return ACTIVE_STATUSES.has(status);
};

const PrivateRoute = () => {
    const { authToken, user } = useAuth();
    const client = useClient();
    const location = useLocation();
    const skipSubscriptionCheck = useMemo(() => {
        if (location.pathname.startsWith("/checkout")) return true;
        return (user?.role || '').toLowerCase() === 'admin';
    }, [location.pathname, user?.role]);
    const [checkingSubscription, setCheckingSubscription] = useState(() => !!authToken);
    const [hasActiveSubscription, setHasActiveSubscription] = useState(true);

    useEffect(() => {
        let alive = true;
        if (!authToken) {
            setCheckingSubscription(false);
            return undefined;
        }
        if (skipSubscriptionCheck) {
            setHasActiveSubscription(true);
            setCheckingSubscription(false);
            return undefined;
        }
        setCheckingSubscription(true);
        const checkSubscription = async () => {
            try {
                const { data } = await client.get("/auth/billing/subscription");
                if (!alive) return;
                setHasActiveSubscription(isActiveSubscription(data));
            } catch (err) {
                if (!alive) return;
                setHasActiveSubscription(false);
            } finally {
                if (alive) setCheckingSubscription(false);
            }
        };
        checkSubscription();
        return () => {
            alive = false;
        };
    }, [authToken, client, skipSubscriptionCheck]);

    if (!authToken) {
        return <Navigate to="/login" replace />;
    }

    if (checkingSubscription) {
        return null;
    }

    if (!hasActiveSubscription) {
        return <Navigate to="/pricing" replace />;
    }

    return <Outlet />;
};

export default PrivateRoute;
